import { AlertTriangle, ArrowLeft } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import { FeeRuleWizard, type FeeRuleMode } from "../components/fees/rule-wizard/FeeRuleWizard";
import { useOrganizationNames } from "../hooks/useOrganizationNames";
import { useOrganizationsCatalog } from "../hooks/useOrganizationsCatalog";
import { usePaymentFees } from "../hooks/usePaymentFees";
import { useRoutesCatalog } from "../hooks/usePaymentFeesCatalogs";
import { PaymentFeeKind, type PaymentFee } from "../types/paymentFees.types";
import { shortId } from "../utils/feeFormat";

const MODES: FeeRuleMode[] = ["new", "version", "duplicate"];

/**
 * `/app/payments-config/fees/rules/new` — a rule wider than one corridor, built step by step. Reads the same
 * `organization_id` scope as the price book, so a rule started from an organization's view is that
 * organization's special price.
 */
const PaymentFeeRulePage = () => {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const { t: tGlobal } = useTranslations("global");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const rawMode = searchParams.get("mode") as FeeRuleMode | null;
  const mode: FeeRuleMode = rawMode && MODES.includes(rawMode) ? rawMode : "new";
  const sourceId = searchParams.get("source");
  const kind =
    searchParams.get("kind") === PaymentFeeKind.PROVIDER_COST
      ? PaymentFeeKind.PROVIDER_COST
      : PaymentFeeKind.COLOCA_COMMISSION;

  const routesCatalog = useRoutesCatalog();
  const organizationsCatalog = useOrganizationsCatalog();
  const feesState = usePaymentFees(routesCatalog.routes);
  const scopeOrganizationId = feesState.scopeOrganizationId;
  const organizationNames = useOrganizationNames(organizationsCatalog.organizations, [scopeOrganizationId]);
  const scopeOrganizationName = scopeOrganizationId
    ? (organizationNames[scopeOrganizationId] ?? shortId(scopeOrganizationId))
    : null;

  const title =
    mode === "version"
      ? t("dialog_title_version")
      : mode === "duplicate"
        ? t("dialog_title_duplicate")
        : t("dialog_title_new");

  useAppShellBreadcrumb(
    useMemo(
      () => [
        { label: tGlobal("sidebar_section_gestion") },
        { label: tGlobal("sidebar_menu_payment_fees"), to: "/app/payments-config/fees" },
        { label: title },
      ],
      [tGlobal, title],
    ),
  );

  const backHref = useMemo(() => {
    const params = new URLSearchParams();
    params.set("tab", kind === PaymentFeeKind.PROVIDER_COST ? "costs" : "commissions");
    if (scopeOrganizationId) params.set("organization_id", scopeOrganizationId);
    return `/app/payments-config/fees?${params.toString()}`;
  }, [kind, scopeOrganizationId]);

  const handleCreated = useCallback(
    (fee: PaymentFee) => {
      colocaToast.success(
        t("toast_created", {
          version: fee.version,
          date: formatDateTime(fee.valid_from, { locale: activeLanguage }),
        }),
      );
      navigate(backHref);
    },
    [navigate, backHref, t, activeLanguage],
  );

  if (!feesState.hasAccess) return <NoAccessView />;

  const source = sourceId ? (feesState.fees.find((fee) => fee.id === sourceId) ?? null) : null;
  const isReady = !feesState.isLoading && !routesCatalog.isLoading;

  return (
    <div className="space-y-6 p-2">
      <ColocaButton variant="ghost" size="sm" icon={ArrowLeft} onClick={() => navigate(backHref)}>
        {t("rule_back")}
      </ColocaButton>
      <ColocaPageHeader title={title} description={t("rule_page_subtitle")} />

      {!isReady ? (
        <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)_19rem]">
          <ColocaSkeleton className="h-40 rounded-xl" />
          <ColocaSkeleton className="h-96 rounded-xl" />
          <ColocaSkeleton className="h-64 rounded-xl" />
        </div>
      ) : sourceId && !source ? (
        <ColocaAlert variant="warning" icon={AlertTriangle} text={t("rule_source_missing")} />
      ) : (
        <FeeRuleWizard
          mode={mode}
          feeKind={source?.fee_kind ?? kind}
          source={source}
          routes={routesCatalog.routes}
          fees={feesState.fees}
          now={feesState.now}
          scopeOrganizationId={scopeOrganizationId}
          scopeOrganizationName={scopeOrganizationName}
          organizationNames={organizationNames}
          exampleAmountFor={feesState.exampleAmountFor}
          onCancel={() => navigate(backHref)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
};

export default PaymentFeeRulePage;
